import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { Environment } from "../utils/Environment";

const apiUrl = Environment.getApiUrl();

const ProcessHydroData: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [outputPath, setOutputPath] = useState("output");
  const [downloading, setDownloading] = useState(false);
  const [pollingJobId, setPollingJobId] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Poll for hydro job status
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    const username = localStorage.getItem("userName") || "";
    if (pollingJobId) {
      setStatusLoading(true);
      interval = setInterval(async () => {
        try {
          const response = await fetch(
            `${apiUrl}/hydro-job-status/${username}/${pollingJobId}`
          );
          if (response.ok) {
            const data = await response.json();
            setJobStatus(data.status);
            setStatusMessage(data.status === "processed"
              ? "Hydraulic data processed successfully!"
              : `Status: ${data.status}`);
            setStatusLoading(false);
            if (
              ["processed", "failed", "error", "unknown", "not_found"].includes(data.status)
            ) {
              setPollingJobId(null); // Stop polling
            }
          } else {
            setStatusMessage("Failed to get job status.");
            setStatusLoading(false);
          }
        } catch (err) {
          setStatusMessage("Error polling job status.");
          setStatusLoading(false);
        }
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [pollingJobId, apiUrl]);

  const handleProcess = async () => {
    setLoading(true);
    setResult(null);
    setJobStatus(null);
    setStatusMessage(null);
    setPollingJobId(null);
    const username = localStorage.getItem("userName") || "";
    const job_id = localStorage.getItem("job_id") || "";
    const client_id = localStorage.getItem("client_id") || "";

    try {
      const response = await fetch(`${apiUrl}/process-hydro-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, job_id, client_id }),
      });
      if (!response.ok)
        throw new Error("Processing failed");
      const data = await response.json();
      if (data.job_id) {
        localStorage.setItem("job_id", data.job_id);
        setPollingJobId(data.job_id); // Start polling
        setStatusMessage(data.message);
      }
      setResult(null); // Clear result, show status via polling
    } catch (err) {
      setResult("Failed to start processing. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadzip = async () => {
    setDownloading(true);
    setResult(null);
    const username = localStorage.getItem("userName") || "";
    const job_id = localStorage.getItem("job_id") || "";
    let filename = outputPath.trim();
    if (!filename.toLowerCase().endsWith("zip")) {
      filename += ".zip";
    }
    try {
      const response = await fetch(`${apiUrl}/download-hydro-zip`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: localStorage.getItem("client_id") || "",
          username,
          job_id,
          package_save_path: filename
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to download zip");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setResult("zip file downloaded!");
    } catch (err) {
      setResult("Failed to download zip.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        pt: 6,
      }}
    >
      <Typography variant="h6" sx={{ mb: 3 }}>
        Ready to process hydraulic data?
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={handleProcess}
        disabled={loading}
        sx={{ mb: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : "Process"}
      </Button>
      <TextField
        label="Output zip Filename"
        value={outputPath}
        onChange={e => setOutputPath(e.target.value)}
        helperText="Name of your downloaded file (e.g., output)"
        sx={{ width: 300, mb: 2 }}
      />
      <Button
        variant="outlined"
        color="secondary"
        disabled={downloading}
        onClick={handleDownloadzip}
        sx={{ mb: 2 }}
      >
        {downloading ? <CircularProgress size={24} /> : "Download zip"}
      </Button>
      {statusLoading || (jobStatus && jobStatus !== "processed" && !["failed", "error", "unknown", "not_found"].includes(jobStatus)) ? (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <Typography variant="body2" sx={{ mr: 1 }}>
            {jobStatus ? `Status: ${jobStatus}` : "Checking status..."}
          </Typography>
          <CircularProgress size={20} />
        </Box>
      ) : null}
      {jobStatus && ["processed", "failed", "error", "unknown", "not_found"].includes(jobStatus) && (
        <Box sx={{ mt: 2, p: 2, border: "1px solid #ccc", borderRadius: 2 }}>
          <Typography variant="h6">Hydro Job Status</Typography>
          <Typography variant="body2">
            Status: {jobStatus}
          </Typography>
          <Typography variant="body2">
            Message: {statusMessage}
          </Typography>
        </Box>
      )}
      {result && (
        <Typography variant="body2" color={result.startsWith("Failed") ? "error" : "success.main"}>
          {result}
        </Typography>
      )}
    </Box>
  );
};

export default ProcessHydroData;